import { Component, Input, OnInit } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { noop } from 'underscore';

import { VideoPlaybackComponent } from 'rev-portal/media/videos/videoPlayback/VideoPlayback.Component';
import { VideoPlaybackService } from 'rev-portal/media/videos/videoPlayback/VideoPlayback.Service';

import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { ConfirmationDialogAngularComponent } from 'rev-shared/ui/dialog/ConfirmationDialogAngular.Component';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';

import menuItemStyles from 'rev-shared/ui/menu-item/menu-item.module.less';

@Component({
	selector: 'video-playback-settings-menu',
	templateUrl: './VideoPlaybackSettingsMenu.Component.html'
})
export class VideoPlaybackSettingsMenuComponent implements OnInit {
	@Input() public mediaFeatures: any;
	@Input() public videoId: string;

	public readonly menuItemStyles = menuItemStyles;

	private isAccountAdminUser: boolean;

	constructor(
		private SecurityContext: SecurityContextService,
		private VideoPlaybackSvc: VideoPlaybackService,
		private Dialog: DialogService,
		private TranslateService: TranslateService,
		public VideoPlaybackComponent: VideoPlaybackComponent
	) { }

	public ngOnInit(): void {
		this.isAccountAdminUser = !!this.SecurityContext.checkAuthorization('admin.accounts.edit');
	}

	private get canManageLegalHold(): boolean {
		return this.isAccountAdminUser && this.mediaFeatures.enableLegalHold;
	}

	private get isLegalHold(): boolean {
		return this.VideoPlaybackComponent.video.legalHold;
	}

	public get showApplyLegalHold(): boolean {
		return this.canManageLegalHold && !this.isLegalHold;
	}

	public get showDeleteVideo(): boolean {
		return this.VideoPlaybackComponent.canEditVideo && !this.isLegalHold;
	}

	public get showEditVideo(): boolean {
		return this.VideoPlaybackComponent.canEditVideo &&
			!this.VideoPlaybackComponent.isLinkSource &&
			this.VideoPlaybackComponent.isReadyToPlay &&
			!this.isLegalHold;
	}

	public get showMenu(): boolean {
		return this.showApplyLegalHold ||
			this.showDeleteVideo ||
			this.showEditVideo ||
			this.showRemoveLegalHold ||
			this.showReplaceVideo ||
			this.showSettings;
	}

	public get showRemoveLegalHold(): boolean {
		return this.canManageLegalHold && this.isLegalHold;
	}

	public get showPreposition(): boolean {
		return this.mediaFeatures.enableIndividualVideoPreposition &&
			this.showSettings &&
			this.VideoPlaybackComponent.isReadyToPlay &&
			!this.VideoPlaybackComponent.isLinkSource &&
			this.VideoPlaybackComponent.hasMediaEditAuth;
	}

	public get showReplaceVideo(): boolean {
		return this.showSettings &&
			!this.VideoPlaybackComponent.isLinkSource;
	}

	public get showSettings(): boolean {
		return this.VideoPlaybackComponent.canEditVideo && !this.isLegalHold;
	}

	public applyLegalHold(): void {
		this.openDialog(
			'Video_LegalHold_Apply_Dialog_Message',
			'Video_LegalHold'
		).result.then(() => {
			this.VideoPlaybackSvc.setLegalHold(this.videoId, true);
		}).catch(() => noop);
	}

	public removeLegalHold(): void {
		this.openDialog(
			'Video_LegalHold_Remove_Dialog_Message',
			'Video_LegalHold'
		).result.then(() => {
			this.VideoPlaybackSvc.setLegalHold(this.videoId, false);
		}).catch(() => noop);
	}

	public delete(): void {
		this.VideoPlaybackComponent.deleteVideo();
	}

	private openDialog(messageKey: string, titleKey: string, actionKey: string = 'Ok', cancelKey: string = 'Cancel'): ConfirmationDialogAngularComponent {
		return this.Dialog.openConfirmationDialog(
			{
				message: this.TranslateService.instant(messageKey),
				title: this.TranslateService.instant(titleKey),
				actionText: this.TranslateService.instant(actionKey),
				cancelText: this.TranslateService.instant(cancelKey)
			}
		);
	}
}
